import React, { useState, useEffect, useCallback } from 'react';
import { adminService } from '../../api/apiClient';
import { useToast } from '../../context/ToastContext';
import { Card } from '../../components/UI/Card';
import { Button } from '../../components/UI/Button';
import { Badge } from '../../components/UI/Badge';
import { Spinner } from '../../components/UI/Spinner';
import {
  Server,
  Database,
  RefreshCw,
  Cpu,
} from 'lucide-react';

export const AdminSystem = () => {
  const [system, setSystem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);

  const { success, error: toastError } = useToast();

  const fetchSystem = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminService.system();
      if (res && res.data) {
        setSystem(res.data);
      }
    } catch (err) {
      toastError('Error al obtener diagnóstico del sistema.');
    } finally {
      setLoading(false);
    }
  }, [toastError]);

  useEffect(() => {
    fetchSystem();
  }, [fetchSystem]);

  const handleClearCache = async () => {
    setClearing(true);
    try {
      const res = await adminService.clearCache();
      success(res.message || 'Caché limpiada y optimizada.');
      fetchSystem();
    } catch (err) {
      toastError(err.message || 'Error al limpiar caché.');
    } finally {
      setClearing(false);
    }
  };

  if (loading) {
    return <Spinner size="lg" label="Obteniendo diagnóstico del servidor..." className="py-20" />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100 tracking-tight flex items-center gap-2">
            <Server className="w-6 h-6 text-[#08707A] dark:text-teal-400" />
            Diagnóstico del Sistema & Mantenimiento
          </h1>
          <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-1">
            Información del entorno backend de Laravel API, optimización de caché y rendimiento
          </p>
        </div>

        <Button
          onClick={handleClearCache}
          isLoading={clearing}
          icon={RefreshCw}
          className="bg-[#08707A] hover:bg-[#065B63]"
        >
          Limpiar Caché del Servidor
        </Button>
      </div>

      {/* Diagnostics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Core PHP & Framework Details */}
        <Card className="p-6 space-y-4">
          <div className="flex items-center gap-2.5">
            <Cpu className="w-5 h-5 text-[#08707A] dark:text-teal-400" />
            <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">
              Entorno de Ejecución Backend
            </h3>
          </div>

          <div className="space-y-3 text-xs">
            <div className="flex justify-between py-2 border-b border-slate-100 dark:border-slate-800">
              <span className="text-slate-500">Versión de PHP:</span>
              <span className="font-mono font-bold text-slate-800 dark:text-slate-200">{system?.php_version}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-slate-100 dark:border-slate-800">
              <span className="text-slate-500">Versión Laravel:</span>
              <span className="font-mono font-bold text-slate-800 dark:text-slate-200">v{system?.laravel_version}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-slate-100 dark:border-slate-800">
              <span className="text-slate-500">Ambiente de App:</span>
              <Badge variant="primary" size="sm">{system?.app_env}</Badge>
            </div>
            <div className="flex justify-between py-2 border-b border-slate-100 dark:border-slate-800">
              <span className="text-slate-500">Modo Debug:</span>
              <Badge variant={system?.app_debug ? 'warning' : 'success'} size="sm">
                {system?.app_debug ? 'Activado' : 'Desactivado'}
              </Badge>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-slate-500">URL Base de API:</span>
              <span className="font-mono text-slate-800 dark:text-slate-200 truncate max-w-xs">{system?.app_url}</span>
            </div>
          </div>
        </Card>

        {/* Database & Drivers */}
        <Card className="p-6 space-y-4">
          <div className="flex items-center gap-2.5">
            <Database className="w-5 h-5 text-[#08707A] dark:text-teal-400" />
            <h3 className="text-sm font-bold text-slate-900 dark:text-slate-100">
              Base de Datos & Almacenamiento
            </h3>
          </div>

          <div className="space-y-3 text-xs">
            <div className="flex justify-between py-2 border-b border-slate-100 dark:border-slate-800">
              <span className="text-slate-500">Controlador de BD:</span>
              <span className="font-mono font-bold text-slate-800 dark:text-slate-200">{system?.db_connection}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-slate-100 dark:border-slate-800">
              <span className="text-slate-500">Manejador de Caché:</span>
              <span className="font-mono font-bold text-slate-800 dark:text-slate-200">{system?.cache_driver}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-slate-100 dark:border-slate-800">
              <span className="text-slate-500">Manejador de Sesión:</span>
              <span className="font-mono font-bold text-slate-800 dark:text-slate-200">{system?.session_driver}</span>
            </div>
            <div className="flex justify-between py-2 border-b border-slate-100 dark:border-slate-800">
              <span className="text-slate-500">Idioma Predeterminado:</span>
              <span className="font-mono font-bold text-slate-800 dark:text-slate-200">{system?.default_locale}</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-slate-500">Hora del Servidor:</span>
              <span className="font-mono text-slate-800 dark:text-slate-200">{system?.server_time}</span>
            </div>
          </div>
        </Card>
      </div>

      {/* Global Records Metric Count */}
      <Card className="p-6">
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-4">
          Conteo de Objetos en Base de Datos
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-center">
          <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50">
            <p className="text-2xl font-black text-slate-900 dark:text-slate-100">{system?.total_users || 0}</p>
            <p className="text-xs text-slate-500 mt-0.5">Usuarios</p>
          </div>
          <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50">
            <p className="text-2xl font-black text-slate-900 dark:text-slate-100">{system?.total_invoices || 0}</p>
            <p className="text-xs text-slate-500 mt-0.5">Facturas</p>
          </div>
          <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50">
            <p className="text-2xl font-black text-slate-900 dark:text-slate-100">{system?.total_quotes || 0}</p>
            <p className="text-xs text-slate-500 mt-0.5">Cotizaciones</p>
          </div>
          <div className="p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50">
            <p className="text-2xl font-black text-slate-900 dark:text-slate-100">{system?.total_products || 0}</p>
            <p className="text-xs text-slate-500 mt-0.5">Productos</p>
          </div>
        </div>
      </Card>
    </div>
  );
};
